import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { TaxGroup } from '@masamune/shared';
import { Icon } from '../../../components/ui/Icon';
import { EmptyState } from '../../../components/ui/EmptyState';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { adminHref } from '../../../lib/adminRoutes';
import { plurale } from '../adminApi';
import { fiscoSintesiQuery, type GruppoCalendario, type Sintesi } from './fiscoApi';
import { Caricamento, Frase, Intestazione, Piega, TabellaTasse, Totale } from './parts';

/**
 * Calendario delle tasse (vista predefinita di Fisco): la frase, poi quattro
 * gruppi con il totale nell'intestazione (proposta § 3.2, nomi della SPEC):
 *   Tasse non versate in tempo · Tasse da pagare nei prossimi 30 giorni ·
 *   Tasse da pagare fra 31 e 90 giorni · Tasse già pagate nel <anno> (chiuso).
 * L'IVA non versata è UNA riga che porta alla vista IVA, non un muro di
 * trimestri. Ordinato per data della prossima rata.
 */
export function CalendarioView({ canWrite, onVai }: { canWrite: boolean; onVai: (g: TaxGroup) => void }) {
  const q = useQuery(fiscoSintesiQuery);
  const d = q.data;
  if (q.isLoading && !d) return <Caricamento />;
  if (!d) return <EmptyState icon="calendarClock" title="Calendario delle tasse non disponibile" description="Non riesco a leggere le tasse in questo momento. Riprova tra poco." />;

  return (
    <>
      <Frase testo={d.frase} />
      <div className="mt-6">
        {d.gruppi.map((g) => <Gruppo key={g.key} g={g} canWrite={canWrite} onVai={onVai} />)}
        {d.piuAvanti.testo && (
          <div className="adm-note mt-4">
            <Icon name="calendarClock" size={12} className="mute" />
            <span>{d.piuAvanti.testo.charAt(0).toUpperCase() + d.piuAvanti.testo.slice(1)}. Li trovi nelle viste per tipo.</span>
          </div>
        )}
      </div>
      <Pagate d={d} onVai={onVai} />
    </>
  );
}

function Gruppo({ g, canWrite, onVai }: { g: GruppoCalendario; canWrite: boolean; onVai: (g: TaxGroup) => void }) {
  const senza = g.senzaCifra > 0 ? `${plurale(g.senzaCifra, 'tassa senza cifra', 'tasse senza cifra')}, fuori dal totale` : null;
  const vuoto: Record<GruppoCalendario['key'], string> = {
    indietro: 'Nessuna tassa non versata in tempo, con i dati di oggi.',
    entro30: 'Nessuna tassa da pagare nei prossimi 30 giorni.',
    entro90: 'Nessuna tassa da pagare fra 31 e 90 giorni.',
  };
  // Con una riga sola il totale del gruppo È quella riga: l'importo si dice una volta, in intestazione.
  const una = g.righe.length === 1;
  return (
    <section className="fis-gruppo">
      <Intestazione
        label={g.label}
        destra={g.righe.length ? <Totale valore={g.totale} nota={senza} /> : null}
      />
      {g.righe.length === 0 ? (
        <div className="fis-vuoto">{vuoto[g.key]}</div>
      ) : (
        <TabellaTasse
          righe={g.righe}
          canWrite={canWrite}
          onVai={onVai}
          senzaImporto={una && g.righe[0]!.importo != null}
          colQuando={g.key === 'indietro' ? 'Da quanto è scaduta' : 'Quando si paga'}
        />
      )}
    </section>
  );
}

/**
 * Le tasse già pagate nell'anno: chiuse, con il totale sempre in vista.
 * Dentro, una riga per tipo (il dettaglio sta nella vista del tipo), e i
 * pagamenti al fisco che in banca non sono collegati a nessuna tassa: si
 * elencano ma non si sommano (SPEC, correzione 6).
 */
function Pagate({ d, onVai }: { d: Sintesi; onVai: (g: TaxGroup) => void }) {
  const navigate = useNavigate();
  const p = d.pagate;
  const banca = adminHref('banca', { vista: 'movimenti', side: 'DEBIT', unmatched: '1', q: 'Agenzia delle Entrate', from: `${d.anno}-01-01` });
  return (
    <section className="fis-gruppo mt-7">
      <Intestazione label={p.label} destra={<Totale valore={p.totale} />} />
      <Piega filo={false} label={`il dettaglio delle tasse già pagate nel ${d.anno}`}>
        {p.perGruppo.length === 0 ? (
          <div className="fis-vuoto">Nessuna tassa pagata nel {d.anno}, con i dati di oggi.</div>
        ) : p.perGruppo.map((g) => (
          <div key={g.group} className="adm-line fis-vai" onClick={() => onVai(g.group)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onVai(g.group); }}>
            <div className="min-w-0 flex-1"><span className="font-semibold fis-vai-t">{g.label}</span></div>
            <div className="mute text-[12px]" style={{ width: 130 }}>{plurale(g.n, 'versamento', 'versamenti')}</div>
            <div className="mono font-semibold text-right" style={{ width: 120 }}>€ {fmtEur(g.totale, true)}</div>
            <Icon name="chevronR" size={10} className="mute" />
          </div>
        ))}
        {p.daClassificare.length > 0 && (
          <div className="mt-5">
            <div className="data-hd">
              <span className="t-micro">Pagamenti al fisco non collegati a nessuna tassa</span>
              <span className="mono mute text-[12px]">{plurale(p.daClassificare.length, 'pagamento', 'pagamenti')}, non sommati</span>
            </div>
            {p.daClassificare.map((x) => (
              <div key={x.id} className="adm-line">
                <div className="mono text-[12px] mute" style={{ width: 110 }} title={fmtDate(x.data)}>{x.dataTesto}</div>
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{x.tipo === 'F24' ? "F24 all'Agenzia delle Entrate" : x.tipo}</span>
                  {x.serie && <span className="mute text-[12px]"> · {x.serie}</span>}
                </div>
                <div className="mono font-semibold text-right" style={{ width: 120 }}>€ {fmtEur(x.importo, true)}</div>
              </div>
            ))}
            <div className="adm-note mt-3">
              <Icon name="help" size={12} className="mute" />
              <span>
                Sono usciti dalla banca verso il fisco ma qui non sono collegati a una tassa, quindi non entrano nel totale.{' '}
                <a className="underline cursor-pointer" onClick={() => navigate(banca)}>Guardali nei movimenti del conto</a>
              </span>
            </div>
          </div>
        )}
      </Piega>
    </section>
  );
}
